import { friendlyError } from '../../../utils/userMessages';
import { resolveCompanyId, normalizeCompanyRecord } from '../../../utils/companyIdentity';
import { brandPalette as palette, getContrastText, getReadableColor, getGradientContrastText } from '../../../utils/brandPalette';
// src/components/companies/CompanyProfile/CompanyInfo.js

import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Avatar,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Skeleton,
    Stack,
    Badge,
    LinearProgress,
    Tabs,
    Tab,
    Alert,
    CircularProgress,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CardMedia,
    Fab,
    Backdrop,
    Zoom,
    Snackbar,
    Fade,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Popover,
    InputAdornment,
    alpha,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckCircleIcon,
    Verified as VerifiedIcon,
    People as PeopleIcon,
    Event as EventIcon,
    Schedule as ScheduleIcon,
    Chat as ChatIcon,
    Send as SendIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Language as LanguageIcon,
    LinkedIn as LinkedInIcon,
    Public as PublicIcon,
    CalendarToday as CalendarIcon,
    FiberManualRecord as LiveIcon,
    AutoAwesome as AiIcon,
    Storefront as StorefrontIcon,
    Group as GroupIcon,
    Info as InfoIcon,
    Edit as EditIcon,
    Menu as MoreVertIcon,
    Pending as PendingIcon,
    Visibility as VisibilityIcon,
    Settings as SettingsIcon,
    Share as ShareIcon,
    Description as CopyIcon,
    Dashboard as DashboardIcon,
    Flag as FlagIcon,
    Stars as StarsIcon,
    Diamond as DiamondIcon,
    Handshake as HandshakeIcon,
    RocketLaunch as RocketIcon,
    TrendingUp as TrendingUpIcon,
    PhotoCamera as PhotoCameraIcon,
    Image as ImageIcon,
    Close as CloseIcon,
    ImageOutlined as AddPhotoIcon,
    Save as SaveIcon,
    Download as DownloadIcon,
    Description as FileCopyIcon,
    EmojiEvents as EmojiEventsIcon,
    WorkspacePremium as PremiumIcon,
    Receipt as ReceiptIcon,
    Receipt as QrCodeIcon,
    VerifiedUser as VerifiedUserIcon,
    Security as SecurityIcon,
    Check as CheckIcon,
    Inventory2 as CategoryIcon,
    BadgeOutlined as WorkIcon,
    LocationOn as MapIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Search as SearchIcon2,
    Close as ClearIcon,
    PersonAdd as GroupAddIcon,
    Restore as HistoryIcon,
    NetworkCheck as WifiIcon,
    NetworkCheck as CellTowerIcon,
    NetworkCheck as RouterIcon,
    SettingsApplications as HubIcon,
    Speed as SpeedIcon,
    Cloud as CloudIcon,
    NetworkCheck as SignalBarsIcon,
    Block as BlockIcon,
    Person as PersonIcon,
    Description as TitleIcon,
    ShoppingBagRounded as SellIcon,
    Subscriptions as SubscriptionsIcon,
    Person as AccountCircleIcon,
    ShoppingBagRounded as ShoppingCartIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../auth/AuthContext';
import { db } from '../../../config/firebase';
import { 
    doc, 
    getDoc, 
    collection, 
    query, 
    where, 
    getDocs,
    updateDoc,
    serverTimestamp,
    addDoc,
    onSnapshot
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { safeExternalUrl } from '../../../utils/safeNavigation';
import { uploadFile, deleteFile } from '../../../services/integrations/storage';
import { updateCompanyProfile as updateCompanyProfileSecure } from '../../../services/integrations/backend';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Header from '../../common/Header/Header';
import LoadingSpinner from '../../common/LoadingSpinner';
import BrandLogo from '../../common/BrandLogo';
import { getCompanyLogoUrl, getCompanyCoverUrl } from '../../../utils/companyIdentity';

// ============================================
// PALETTE
// ============================================



const SHADOW = '25, 57, 78';
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BOUNCE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

// ============================================================================
// ELEVATION LADDER
// ============================================================================

const ELEVATION = {
    0: { ambY: 2,  ambB: 8,   ambA: 0.05, conY: 1,  conB: 2,  conA: 0.04, blur: 8,  rake: 0.5,  fill: 0.34 },
    1: { ambY: 8,  ambB: 24,  ambA: 0.09, conY: 2,  conB: 6,  conA: 0.05, blur: 14, rake: 0.68, fill: 0.5 },
    2: { ambY: 16, ambB: 44,  ambA: 0.12, conY: 4,  conB: 12, conA: 0.07, blur: 20, rake: 0.82, fill: 0.62 },
    3: { ambY: 28, ambB: 70,  ambA: 0.15, conY: 7,  conB: 20, conA: 0.08, blur: 26, rake: 0.92, fill: 0.7 },
    4: { ambY: 42, ambB: 104, ambA: 0.18, conY: 11, conB: 30, conA: 0.09, blur: 32, rake: 1,    fill: 0.78 },
};

// ============================================================================
// GLASS STYLES
// ============================================================================

const glassCard = ({ level = 2, radius = '14px', tint = palette.paper, accent = null } = {}) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: radius,
    background: accent
        ? `radial-gradient(circle at 100% 0%, ${alpha(accent,.075)}, transparent 34%), linear-gradient(145deg, #FFFFFF 0%, ${alpha(accent,.025)} 100%)`
        : 'linear-gradient(145deg, #FFFFFF 0%, #F9FCFD 100%)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.075)}`,
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : level === 0
            ? '0 2px 7px rgba(25,57,78,.035)'
            : '0 5px 18px rgba(25,57,78,.055)',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
});

const glassLift = (level = 3, accent = palette.rust) => ({
    transform: 'translateY(-2px)',
    borderColor: alpha(accent, 0.14),
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : '0 8px 20px rgba(25,57,78,.07)',
});

const glassWell = (radius = '10px', accent = null) => ({
    borderRadius: radius,
    background: accent ? alpha(accent, 0.045) : '#F4F8FB',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.065)}`,
    boxShadow: 'none',
});

const glassPod = (color, size = 38, radius = '10px') => ({
    width: size,
    height: size,
    borderRadius: radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: getContrastText(color),
    background: `linear-gradient(145deg, ${color}, ${alpha(color, .72)})`,
    border: '1px solid rgba(255,255,255,.68)',
    boxShadow: `0 7px 16px ${alpha(color,.22)}, inset 0 1px 0 rgba(255,255,255,.48)`,
});

const glassPill = (color, filled = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    minHeight: 22,
    borderRadius: '999px',
    fontSize: '10.5px',
    lineHeight: 1,
    fontWeight: 650,
    whiteSpace: 'nowrap',
    color: filled ? getContrastText(color) : getReadableColor(color),
    background: filled ? color : alpha(color, .07),
    border: `1px solid ${filled ? alpha(color, .12) : alpha(color, .10)}`,
    boxShadow: 'none',
});

const glassButton = (color, deep) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    color: getGradientContrastText(color, deep || color),
    minHeight: 34,
    paddingInline: 12,
    background: `linear-gradient(135deg, ${color}, ${deep || color})`,
    border: `1px solid ${alpha(color, .12)}`,
    boxShadow: `0 5px 14px ${alpha(color, .16)}`,
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 8px 18px ${alpha(color, .20)}` },
    '&.Mui-disabled': { opacity: .58, color: '#fff' },
});

const glassGhostButton = (color) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    minHeight: 34,
    color: getReadableColor(color),
    background: 'rgba(255,253,250,.92)',
    border: `1px solid ${alpha(color, .15)}`,
    boxShadow: '0 2px 7px rgba(25,57,78,.03)',
    transition: 'background .18s ease, border-color .18s ease',
    '&:hover': { background: alpha(color, .035), borderColor: alpha(color, .22) },
});

// ============================================================================
// GLOBAL CSS
// ============================================================================

const GLOBAL_CSS = `
    @keyframes tfAuroraA {
        0%, 100% { transform: translate3d(0,0,0) scale(1);           opacity: 0.55; }
        33%      { transform: translate3d(60px,-40px,0) scale(1.18); opacity: 0.85; }
        66%      { transform: translate3d(-40px,30px,0) scale(0.92); opacity: 0.5; }
    }
    @keyframes tfAuroraB {
        0%, 100% { transform: translate3d(0,0,0) scale(1.05);        opacity: 0.5; }
        40%      { transform: translate3d(-70px,45px,0) scale(0.88); opacity: 0.8; }
        75%      { transform: translate3d(35px,-30px,0) scale(1.2);  opacity: 0.62; }
    }
    @keyframes tfAuroraC {
        0%, 100% { transform: translate3d(0,0,0) scale(0.95);        opacity: 0.6; }
        50%      { transform: translate3d(50px,55px,0) scale(1.25);  opacity: 0.85; }
    }
    @keyframes tfRingSpin    { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
    @keyframes tfRingSpinRev { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
    @keyframes tfNodeRise {
        0%   { transform: translate3d(0,0,0) scale(0.6);        opacity: 0; }
        12%  { opacity: 0.8; }
        88%  { opacity: 0.4; }
        100% { transform: translate3d(26px,-104vh,0) scale(1.1); opacity: 0; }
    }
    @keyframes tfScanSweep {
        0%   { transform: translate3d(0,-14vh,0); opacity: 0; }
        8%   { opacity: 1; }
        92%  { opacity: 1; }
        100% { transform: translate3d(0,114vh,0); opacity: 0; }
    }
    @keyframes tfSignalWave {
        0%   { transform: scale(0.6); opacity: 0.8; }
        70%  { opacity: 0.12; }
        100% { transform: scale(2.2); opacity: 0; }
    }
    @keyframes tfBreathe {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.06); }
    }
    @keyframes tfSheen {
        0%   { transform: translateX(-130%) skewX(-18deg); }
        100% { transform: translateX(320%)  skewX(-18deg); }
    }
    @keyframes tfFloatIcon {
        0%, 100% { transform: translate3d(0,0,0) rotate(0deg);     opacity: 0.09; }
        50%      { transform: translate3d(0,-16px,0) rotate(5deg); opacity: 0.18; }
    }
    @keyframes tfGrain {
        0%   { transform: translate3d(0,0,0); }
        20%  { transform: translate3d(-2%,2%,0); }
        40%  { transform: translate3d(2%,-1%,0); }
        60%  { transform: translate3d(-1%,-2%,0); }
        80%  { transform: translate3d(1%,1%,0); }
        100% { transform: translate3d(0,0,0); }
    }
    @keyframes tfEdgeShift {
        0%   { background-position: 0% 0%; }
        100% { background-position: 300% 0%; }
    }
    @keyframes tfSkeleton {
        0%   { transform: translateX(-120%); }
        100% { transform: translateX(320%); }
    }
    @keyframes tfPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50%      { opacity: 0.5; transform: scale(0.8); }
    }

    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
        }
    }
`;

const FONT_HREF =
    'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700' +
    '&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';

let assetsInjected = false;
const injectAssets = () => {
    if (assetsInjected || typeof document === 'undefined') return;
    assetsInjected = true;
    const link = (rel, href, cross) => {
        const el = document.createElement('link');
        el.rel = rel;
        el.href = href;
        if (cross) el.crossOrigin = 'anonymous';
        document.head.appendChild(el);
    };
    link('preconnect', 'https://fonts.googleapis.com');
    link('preconnect', 'https://fonts.gstatic.com', true);
    link('stylesheet', FONT_HREF);

    const style = document.createElement('style');
    style.setAttribute('data-tf', 'companyinfo');
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
};

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ============================================================================
// AURORA BACKDROP
// ============================================================================

const CLOUDS = [
    { color: palette.rust,   size: 620, top: '-16%', left: '-8%',  dur: 34, delay: 0,   anim: 'tfAuroraA', depth: 26 },
    { color: palette.pine,   size: 520, top: '8%',   left: '68%',  dur: 42, delay: 3,   anim: 'tfAuroraB', depth: -18 },
    { color: palette.gold,   size: 440, top: '56%',  left: '58%',  dur: 46, delay: 5,   anim: 'tfAuroraA', depth: 22 },
    { color: palette.teal,   size: 400, top: '44%',  left: '-12%', dur: 30, delay: 1.5, anim: 'tfAuroraC', depth: -14 },
    { color: palette.purple, size: 360, top: '24%',  left: '34%',  dur: 36, delay: 2.5, anim: 'tfAuroraB', depth: 10 },
];

const GLYPHS = [
    { Icon: WifiIcon,       x: '6%',  y: '20%', size: 28, dur: 6,   delay: 0 },
    { Icon: CellTowerIcon,  x: '90%', y: '12%', size: 32, dur: 7.5, delay: 0.5 },
    { Icon: RouterIcon,     x: '11%', y: '74%', size: 24, dur: 6.5, delay: 1 },
    { Icon: HubIcon,        x: '84%', y: '68%', size: 26, dur: 8,   delay: 1.5 },
    { Icon: SpeedIcon,      x: '44%', y: '7%',  size: 22, dur: 7,   delay: 2 },
    { Icon: CloudIcon,      x: '74%', y: '90%', size: 30, dur: 6.8, delay: 2.5 },
    { Icon: SignalBarsIcon, x: '94%', y: '44%', size: 24, dur: 6.2, delay: 3.5 },
];

const NODES = [
    { s: 6, l: '12%', t: '94%', d: 19, delay: 0,   c: palette.rust },
    { s: 4, l: '26%', t: '97%', d: 24, delay: 3,   c: palette.teal },
    { s: 7, l: '42%', t: '92%', d: 21, delay: 6,   c: palette.rust },
    { s: 5, l: '57%', t: '99%', d: 27, delay: 1.5, c: palette.pine },
    { s: 4, l: '70%', t: '95%', d: 23, delay: 8,   c: palette.gold },
    { s: 6, l: '85%', t: '91%', d: 30, delay: 4.5, c: palette.teal },
    { s: 5, l: '34%', t: '98%', d: 26, delay: 11,  c: palette.purple },
    { s: 4, l: '95%', t: '96%', d: 22, delay: 7,   c: palette.pine },
];

const BroadcastRings = memo(({ size, opacity, reverse }) => (
    <Box component="svg" viewBox="0 0 400 400" sx={{ width: size, height: size, opacity, overflow: 'visible' }}>
        <g fill="none">
            <circle cx="200" cy="200" r="80" stroke={palette.rust} strokeWidth="1" strokeDasharray="3 10"
                    style={{ transformOrigin: '200px 200px', animation: `${reverse ? 'tfRingSpinRev' : 'tfRingSpin'} 48s linear infinite` }} />
            <circle cx="200" cy="200" r="132" stroke={palette.gold} strokeWidth="1" strokeDasharray="1 14"
                    style={{ transformOrigin: '200px 200px', animation: `${reverse ? 'tfRingSpin' : 'tfRingSpinRev'} 64s linear infinite` }} />
            <circle cx="200" cy="200" r="186" stroke={palette.teal} strokeWidth="1" strokeDasharray="6 18"
                    style={{ transformOrigin: '200px 200px', animation: `${reverse ? 'tfRingSpinRev' : 'tfRingSpin'} 86s linear infinite` }} />
        </g>
    </Box>
));
BroadcastRings.displayName = 'BroadcastRings';

const AuroraBackdrop = memo(() => {
    const rootRef = useRef(null);

    useEffect(() => {
        if (typeof window === 'undefined' || prefersReducedMotion()) return undefined;
        const el = rootRef.current;
        if (!el) return undefined;

        let ticking = false;
        let mx = 0;
        let my = 0;

        const apply = () => {
            ticking = false;
            el.style.setProperty('--mx', mx.toFixed(4));
            el.style.setProperty('--my', my.toFixed(4));
        };
        const onMove = (e) => {
            mx = e.clientX / window.innerWidth - 0.5;
            my = e.clientY / window.innerHeight - 0.5;
            if (!ticking) { ticking = true; requestAnimationFrame(apply); }
        };

        window.addEventListener('mousemove', onMove, { passive: true });
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    return (
        <Box
            ref={rootRef}
            aria-hidden="true"
            sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0, bgcolor: palette.canvas, '--mx': 0, '--my': 0 }}
        >
            <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                    radial-gradient(820px circle at 4% -8%,   ${alpha(palette.rust, 0.14)}, transparent 60%),
                    radial-gradient(680px circle at 96% 10%,  ${alpha(palette.pine, 0.11)}, transparent 58%),
                    radial-gradient(720px circle at 50% 110%, ${alpha(palette.gold, 0.1)}, transparent 60%),
                    radial-gradient(560px circle at 78% 64%,  ${alpha(palette.purple, 0.08)}, transparent 58%)
                `,
            }} />

            <Box
                component="svg"
                width="100%"
                height="100%"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.14,
                    transform: 'translate3d(calc(var(--mx) * -14px), calc(var(--my) * -14px), 0)',
                    transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
                    maskImage: 'radial-gradient(ellipse 92% 76% at 50% 40%, #000 26%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 92% 76% at 50% 40%, #000 26%, transparent 78%)',
                }}
            >
                <defs>
                    <pattern id="tfCoGrid" width="60" height="60" patternUnits="userSpaceOnUse">
                        <path d="M 60 0 L 0 0 0 60" fill="none" stroke={palette.rust} strokeWidth="0.5" />
                        <circle cx="0" cy="0" r="1.5" fill={palette.pine} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#tfCoGrid)" />
            </Box>

            {CLOUDS.map((c, i) => (
                <Box key={i} sx={{
                    position: 'absolute',
                    top: c.top,
                    left: c.left,
                    width: c.size,
                    height: c.size,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(c.color, 0.2)} 0%, ${alpha(c.color, 0.07)} 42%, transparent 70%)`,
                    animation: `${c.anim} ${c.dur}s ease-in-out ${c.delay}s infinite`,
                    willChange: 'transform',
                }}>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        transform: `translate3d(calc(var(--mx) * ${c.depth}px), calc(var(--my) * ${c.depth}px), 0)`,
                        transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)',
                    }} />
                </Box>
            ))}

            <Box sx={{
                position: 'absolute', top: '-10%', right: '-16%',
                transform: 'translate3d(calc(var(--mx) * 30px), calc(var(--my) * 30px), 0)',
                transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
            }}>
                <BroadcastRings size={680} opacity={0.38} />
            </Box>
            <Box sx={{
                position: 'absolute', bottom: '-22%', left: '-18%',
                transform: 'translate3d(calc(var(--mx) * -22px), calc(var(--my) * -22px), 0)',
                transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
            }}>
                <BroadcastRings size={580} opacity={0.26} reverse />
            </Box>

            {GLYPHS.map(({ Icon, x, y, size, dur, delay }, i) => (
                <Box key={i} sx={{
                    position: 'absolute', left: x, top: y, color: palette.rust, opacity: 0.09,
                    animation: `tfFloatIcon ${dur}s ease-in-out ${delay}s infinite`,
                    willChange: 'transform, opacity',
                }}>
                    <Icon sx={{ fontSize: size }} />
                </Box>
            ))}

            {NODES.map((n, i) => (
                <Box key={i} sx={{
                    position: 'absolute', left: n.l, top: n.t, width: n.s, height: n.s,
                    borderRadius: '50%', background: n.c, boxShadow: `0 0 12px ${n.c}`, opacity: 0,
                    animation: `tfNodeRise ${n.d}s linear ${n.delay}s infinite`,
                    willChange: 'transform, opacity',
                }} />
            ))}

            <Box sx={{
                position: 'absolute', left: 0, right: 0, top: 0, height: '26vh',
                background: `linear-gradient(180deg, transparent, ${alpha(palette.rust, 0.045)} 55%, transparent)`,
                animation: 'tfScanSweep 12s linear infinite',
                willChange: 'transform, opacity',
            }} />

            <Box sx={{
                position: 'absolute', inset: 0, opacity: 0.3, mixBlendMode: 'overlay',
                backgroundImage:
                    'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27100%27 height=%27100%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.8%27 numOctaves=%273%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.05%27/%3E%3C/svg%3E")',
                animation: 'tfGrain 9s steps(6) infinite',
                willChange: 'transform',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0,
                background: `radial-gradient(ellipse 120% 92% at 50% 45%, transparent 54%, ${alpha(palette.ink, 0.08)} 100%)`,
            }} />
        </Box>
    );
});
AuroraBackdrop.displayName = 'AuroraBackdrop';

// ============================================================================
// SHARED BITS
// ============================================================================

const SectionRule = ({ color = palette.rust, width = 44 }) => (
    <Box sx={{
        width,
        height: 4,
        borderRadius: 999,
        background: `linear-gradient(90deg, ${color}, ${alpha(color, 0)})`,
        boxShadow: `0 2px 8px ${alpha(color, 0.35)}, inset 0 1px 0 rgba(255,255,255,0.7)`,
        mt: 0.85,
    }} />
);

const Reveal = ({ children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 26, scale: 0.985 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
        style={{ height: '100%', width: '100%' }}
    >
        {children}
    </motion.div>
);

// ============================================================================
// STATUS CHIP
// ============================================================================

const PremiumStatusChip = ({ status }) => {
    const statusConfig = {
        active: { label: 'Active', color: palette.pine, icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
        blocked: { label: 'Blocked', color: palette.clay, icon: <BlockIcon sx={{ fontSize: 12 }} /> },
        pending: { label: 'Pending', color: palette.gold, icon: <PendingIcon sx={{ fontSize: 12 }} /> },
        verified: { label: 'Verified', color: palette.pine, icon: <VerifiedIcon sx={{ fontSize: 12 }} /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
        <Chip
            icon={config.icon}
            label={config.label}
            size="small"
            sx={{
                ...glassPill(config.color, true),
                height: 24,
                fontSize: '0.62rem',
            }}
        />
    );
};

// ============================================================================
// INFO CARD WITH GLASS DESIGN
// ============================================================================

const InfoCard = ({ icon, label, value, color, iconBg }) => (
    <Reveal delay={0.02}>
        <Box sx={{
            ...glassCard({ level: 1, radius: '14px', accent: color }),
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
                transform: 'translateY(-3px)',
                ...glassLift(2, color),
            }
        }}>
            <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: iconBg || `${color}15`,
                color: color,
                flexShrink: 0,
            }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: palette.inkSoft, display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ color: palette.ink }}>
                    {value || 'N/A'}
                </Typography>
            </Box>
        </Box>
    </Reveal>
);

// ============================================================================
// STAT CARD WITH OVERLAY ICON
// ============================================================================

const PremiumStatCard = ({ title, value, icon, overlayIcon, color, gradient, delay, loading }) => {
    const nodeRef = useRef(null);
    const hasRun = useRef(false);

    useEffect(() => {
        const node = nodeRef.current;
        if (!node) return undefined;

        if (typeof value !== 'number' || value <= 0) {
            node.textContent = '0';
            return undefined;
        }
        if (hasRun.current) {
            node.textContent = value.toLocaleString();
            return undefined;
        }

        let raf = null;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting || hasRun.current) return;
                hasRun.current = true;
                const start = performance.now();
                const tick = (now) => {
                    const t = Math.min(1, (now - start) / 900);
                    const eased = 1 - Math.pow(1 - t, 3);
                    if (nodeRef.current) nodeRef.current.textContent = Math.round(value * eased).toLocaleString();
                    if (t < 1) raf = requestAnimationFrame(tick);
                };
                raf = requestAnimationFrame(tick);
                observer.disconnect();
            },
            { threshold: 0.3 }
        );
        observer.observe(node);

        return () => {
            observer.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [value]);

    return (
        <Reveal delay={delay}>
            <motion.div whileHover={{ y: -4 }} transition={{ type: 'spring', stiffness: 400, damping: 17 }} style={{ height: '100%' }}>
                <Paper
                    elevation={0}
                    sx={{
                        ...glassCard({ level: 2, radius: '14px', accent: color }),
                        height: '100%',
                        minHeight: 130,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        transition: `transform 0.4s ${EASE}, box-shadow 0.4s ease`,
                        '&:hover': {
                            transform: 'translateY(-6px)',
                            ...glassLift(4, color),
                            '& .stat-glow': { opacity: 0.8, transform: 'scale(1.25)' },
                            '& .stat-pod': { transform: 'scale(1.08) rotate(-6deg)' },
                            '& .stat-sheen': { animation: `tfSheen 0.9s ${EASE}` },
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '8%',
                            right: '8%',
                            height: '2px',
                            borderRadius: '0 0 999px 999px',
                            background: `linear-gradient(90deg, transparent, ${alpha(color, 0.85)}, transparent)`,
                            pointerEvents: 'none',
                            zIndex: 2,
                        },
                    }}
                >
                    <Box className="stat-sheen" sx={{
                        position: 'absolute', top: 0, bottom: 0, left: 0, width: '38%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                        transform: 'translateX(-130%) skewX(-18deg)',
                        pointerEvents: 'none', zIndex: 2,
                    }} />

                    <Box className="stat-glow" sx={{
                        position: 'absolute', top: -70, right: -70, width: 180, height: 180, borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(color, 0.3)}, transparent 70%)`,
                        opacity: 0.35, transition: `all 0.6s ${EASE}`, pointerEvents: 'none',
                    }} />
                    <Box sx={{
                        position: 'absolute', inset: 0, opacity: 0.05,
                        backgroundImage: `radial-gradient(${color} 0.5px, transparent 0.5px)`,
                        backgroundSize: '16px 16px',
                        maskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                        WebkitMaskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                        pointerEvents: 'none',
                    }} />

                    {/* Background Watermark Icon */}
                    {overlayIcon && (
                        <Box
                            sx={{
                                position: 'absolute',
                                right: -8,
                                bottom: -12,
                                opacity: 0.07,
                                transform: 'rotate(-12deg) scale(1.2)',
                                pointerEvents: 'none',
                                '& svg': { fontSize: 110, color: color },
                            }}
                        >
                            {overlayIcon}
                        </Box>
                    )}

                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, p: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Box className="stat-pod" sx={glassPod(color, 46, '14px')}>
                                {React.cloneElement(icon, { sx: { fontSize: 21 } })}
                            </Box>
                            <Box sx={{ ...glassPill(palette.pine), px: 1.2, height: 22, fontSize: '0.52rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                <Box sx={{ position: 'relative', width: 6, height: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: palette.pine }} />
                                    <Box sx={{
                                        position: 'absolute', width: 14, height: 14, borderRadius: '50%',
                                        border: `1px solid ${palette.pine}`, opacity: 0,
                                        animation: 'tfSignalWave 2.4s ease-out infinite',
                                    }} />
                                </Box>
                                Live
                            </Box>
                        </Box>

                        {loading ? (
                            <Skeleton width="50%" height={32} sx={{ bgcolor: palette.hairline }} />
                        ) : (
                            <Typography sx={{
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontWeight: 700,
                                fontSize: 'clamp(1.9rem, 3.6vw, 2.4rem)',
                                lineHeight: 1.1,
                                letterSpacing: '-0.02em',
                                background: `linear-gradient(145deg, ${palette.ink}, ${color})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                display: 'inline-block',
                                mb: 0.25,
                            }}>
                                <span ref={nodeRef}>0</span>
                            </Typography>
                        )}

                        <Typography sx={{
                            color: palette.inkSoft, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.08em', fontSize: '0.65rem',
                        }}>
                            {title}
                        </Typography>
                    </Box>
                </Paper>
            </motion.div>
        </Reveal>
    );
};

// ============================================================================
// COUNTRY SELECTOR POPOVER
// ============================================================================

const CountrySelector = ({ open, anchorEl, onClose, onSelect, selectedCountry }) => {
    const [search, setSearch] = useState('');

    const countries = [
        { code: 'AF', name: 'Afghanistan', dialCode: '+93', flag: '🇦🇫' },
        { code: 'AL', name: 'Albania', dialCode: '+355', flag: '🇦🇱' },
        { code: 'DZ', name: 'Algeria', dialCode: '+213', flag: '🇩🇿' },
        { code: 'AD', name: 'Andorra', dialCode: '+376', flag: '🇦🇩' },
        { code: 'AO', name: 'Angola', dialCode: '+244', flag: '🇦🇴' },
        { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
        { code: 'AM', name: 'Armenia', dialCode: '+374', flag: '🇦🇲' },
        { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
        { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
        { code: 'AZ', name: 'Azerbaijan', dialCode: '+994', flag: '🇦🇿' },
        { code: 'BS', name: 'Bahamas', dialCode: '+1-242', flag: '🇧🇸' },
        { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: '🇧🇭' },
        { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
        { code: 'BB', name: 'Barbados', dialCode: '+1-246', flag: '🇧🇧' },
        { code: 'BY', name: 'Belarus', dialCode: '+375', flag: '🇧🇾' },
        { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪' },
        { code: 'BZ', name: 'Belize', dialCode: '+501', flag: '🇧🇿' },
        { code: 'BJ', name: 'Benin', dialCode: '+229', flag: '🇧🇯' },
        { code: 'BT', name: 'Bhutan', dialCode: '+975', flag: '🇧🇹' },
        { code: 'BO', name: 'Bolivia', dialCode: '+591', flag: '🇧🇴' },
        { code: 'BA', name: 'Bosnia', dialCode: '+387', flag: '🇧🇦' },
        { code: 'BW', name: 'Botswana', dialCode: '+267', flag: '🇧🇼' },
        { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
        { code: 'BN', name: 'Brunei', dialCode: '+673', flag: '🇧🇳' },
        { code: 'BG', name: 'Bulgaria', dialCode: '+359', flag: '🇧🇬' },
        { code: 'BF', name: 'Burkina Faso', dialCode: '+226', flag: '🇧🇫' },
        { code: 'BI', name: 'Burundi', dialCode: '+257', flag: '🇧🇮' },
        { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
        { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
        { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
        { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
        { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
        { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
        { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
        { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
        { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
        { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
    ];

    const filteredCountries = countries.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.dialCode.includes(search) ||
        c.code.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: {
                sx: {
                    ...glassCard({ level: 3, radius: '16px' }),
                    width: 320,
                    maxHeight: 400,
                    overflow: 'hidden',
                }
            } }}
        >
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${palette.hairline}` }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search country..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                        ...glassWell('10px'),
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { border: 'none' },
                            '& input': { fontSize: '0.85rem', py: 0.75 }
                        }
                    }}
                    slotProps={{ input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon2 sx={{ fontSize: 16, color: palette.inkSoft }} />
                            </InputAdornment>
                        ),
                        endAdornment: search && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')}>
                                    <ClearIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </InputAdornment>
                        )
                    } }}
                />
            </Box>
            <Box sx={{ overflowY: 'auto', maxHeight: 320 }}>
                {filteredCountries.map((country) => (
                    <MenuItem
                        key={country.code}
                        onClick={() => { onSelect(country); onClose(); }}
                        sx={{
                            py: 1,
                            px: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderBottom: `1px solid ${palette.hairline}`,
                            '&:hover': { bgcolor: `${palette.rust}08` },
                            '&.Mui-selected': { bgcolor: `${palette.rust}12` }
                        }}
                    >
                        <Typography sx={{ fontSize: 20 }}>{country.flag}</Typography>
                        <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: selectedCountry?.code === country.code ? 600 : 400 }}>
                            {country.name}
                        </Typography>
                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem' }}>
                            {country.dialCode}
                        </Typography>
                        {selectedCountry?.code === country.code && (
                            <CheckCircleIcon sx={{ color: palette.rust, fontSize: 16 }} />
                        )}
                    </MenuItem>
                ))}
                {filteredCountries.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.85rem' }}>
                            No countries found
                        </Typography>
                    </Box>
                )}
            </Box>
        </Popover>
    );
};

// ============================================================================
// CERTIFICATE VIEW COMPONENT
// ============================================================================

const certificateList = (...values) => {
    const flattened = values.flatMap((value) => {
        if (Array.isArray(value)) return value;
        if (value === null || value === undefined || value === '') return [];
        return [value];
    });
    const normalized = flattened
        .map((value) => {
            if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
            if (typeof value === 'object') return String(value.label || value.name || value.value || value.title || '').trim();
            return '';
        })
        .filter(Boolean);
    return Array.from(new Set(normalized));
};

const certificateDate = (value) => {
    const date = value?.toDate?.() || (value?.seconds ? new Date(value.seconds * 1000) : value ? new Date(value) : new Date());
    return Number.isNaN(date.getTime()) ? new Date() : date;
};

const CertificateView = ({ data, onClose }) => {
    const certificateRef = useRef(null);
    const [downloading, setDownloading] = useState(false);

    const companyLogo = getCompanyLogoUrl(data || {});
    const services = certificateList(
        data?.servicesProvided,
        data?.primaryServiceProvided,
        data?.secondaryServiceProvided,
        data?.primaryService,
        data?.secondaryService,
        data?.services,
        data?.specialization
    );
    const regionPriorities = certificateList(
        data?.regionPriorities,
        data?.primaryRegionAvailability,
        data?.secondaryRegionAvailability,
        data?.preferredRegion,
        data?.secondaryPreferredRegion,
        data?.primaryRegion,
        data?.secondaryRegion
    );
    const lookingFor = certificateList(
        data?.lookingFor,
        data?.primaryServiceBuying,
        data?.secondaryServiceBuying,
        data?.primaryConnectWith,
        data?.secondaryConnectWith,
        data?.otherConnectWith
    );
    const registrationNumber = useMemo(() => {
        if (data?.registrationNumber) return data.registrationNumber;
        const identity = String(data?.companyId || data?.id || data?.companyName || 'COMPANY')
            .replace(/[^a-z0-9]/gi, '')
            .toUpperCase()
            .slice(-8)
            .padStart(6, '0');
        const issued = certificateDate(data?.createdAt || data?.verificationDate);
        const stamp = `${issued.getFullYear()}${String(issued.getMonth() + 1).padStart(2, '0')}`;
        return `TFC-${identity}-${stamp}`;
    }, [data?.registrationNumber, data?.companyId, data?.id, data?.companyName, data?.createdAt, data?.verificationDate]);

    const generateCertificatePDF = async () => {
        if (!certificateRef.current) return;
        setDownloading(true);

        try {
            const canvas = await html2canvas(certificateRef.current, {
                scale: 2.5,
                useCORS: true,
                logging: false,
                backgroundColor: '#F1F6F9',
                windowWidth: certificateRef.current.scrollWidth,
                windowHeight: certificateRef.current.scrollHeight,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: [canvas.width, canvas.height],
            });

            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            pdf.save(`Certificate_of_Registration_${data?.companyName?.replace(/\s/g, '_') || 'Company'}.pdf`);

            toast.success('Certificate downloaded successfully!');
        } catch (error) {
            console.error('Error generating certificate:', error);
            toast.error('Failed to generate certificate. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth="md"
            slotProps={{ paper: {
                sx: {
                    borderRadius: '16px',
                    background: 'transparent',
                    boxShadow: 'none',
                    overflow: 'hidden',
                    width: '100%',
                    maxWidth: '960px',
                    m: 2,
                }
            } }}
        >
            <Box sx={{
                position: 'relative',
                bgcolor: '#F1F6F9',
                borderRadius: '16px',
                overflow: 'hidden',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Background Orbs */}
                <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                    <Box sx={{ position: 'absolute', top: '-20%', left: '-10%', width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(193,84,31,0.10) 0%, transparent 70%)', filter: 'blur(50px)' }} />
                    <Box sx={{ position: 'absolute', bottom: '-20%', right: '-10%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, rgba(47,89,66,0.06) 0%, transparent 70%)', filter: 'blur(50px)' }} />
                </Box>

                <Box sx={{
                    position: 'relative',
                    zIndex: 1,
                    p: { xs: 2, sm: 3, md: 4 },
                    overflowY: 'auto',
                    flex: 1,
                }}>
                    <Box ref={certificateRef} sx={{
                        position: 'relative',
                        ...glassCard({ level: 3, radius: '18px', accent: palette.gold }),
                        p: { xs: 2.5, sm: 3.5, md: 4.5 },
                        width: '100%',
                        maxWidth: '900px',
                        mx: 'auto',
                        background: 'linear-gradient(145deg, #FFFFFF 0%, #F4F8FB 100%)',
                    }}>
                        {/* Decorative borders */}
                        <Box sx={{ position: 'absolute', inset: 10, border: '2px solid rgba(193,84,31,0.06)', borderRadius: '18px', pointerEvents: 'none' }} />
                        <Box sx={{ position: 'absolute', inset: 15, border: '1px solid rgba(184,132,42,0.05)', borderRadius: '14px', pointerEvents: 'none' }} />

                        {/* Corner decorations */}
                        {[
                            { top: 14, left: 14, transform: 'rotate(0deg)' },
                            { top: 14, right: 14, transform: 'rotate(90deg)' },
                            { bottom: 14, left: 14, transform: 'rotate(-90deg)' },
                            { bottom: 14, right: 14, transform: 'rotate(180deg)' },
                        ].map((pos, i) => (
                            <Box key={i} sx={{ position: 'absolute', ...pos, width: 28, height: 28, opacity: 0.25, pointerEvents: 'none' }}>
                                <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
                                    <path d="M0 40L0 20C0 8.95431 8.95431 0 20 0L40 0" stroke="#c1541f" strokeWidth="2.5"/>
                                    <path d="M5 35L5 20C5 11.7157 11.7157 5 20 5L35 5" stroke="#b8842a" strokeWidth="1.5" opacity="0.5"/>
                                </svg>
                            </Box>
                        ))}

                        {/* Ornamental top border */}
                        <Box sx={{
                            height: 3,
                            width: '50%',
                            mx: 'auto',
                            mb: 2,
                            borderRadius: 4,
                            background: 'linear-gradient(90deg, transparent, #c1541f, #b8842a, #2f5942, transparent)',
                            opacity: 0.4
                        }} />

                        {/* TelcoFair left · company identity right */}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto 1fr',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 1.8,
                            position: 'relative',
                            zIndex: 2,
                        }}>
                            <motion.div initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: .45 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BrandLogo scope="platform" size={58} radius={13} alt="TELCOFAIR" />
                                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                        <Typography sx={{ fontWeight: 900, color: palette.ink, fontSize: '.88rem', letterSpacing: '-.025em' }}>TELCOFAIR</Typography>
                                        <Typography sx={{ color: palette.inkFaint, fontSize: '.52rem', fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase' }}>Network Registry</Typography>
                                    </Box>
                                </Box>
                            </motion.div>

                            <Box sx={{ width: 1, height: 46, bgcolor: palette.hairline, display: { xs: 'none', sm: 'block' } }} />

                            <motion.div initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: .45, delay: .08 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                    <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                                        <Typography sx={{ fontWeight: 850, color: palette.ink, fontSize: '.82rem', maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data?.companyName || 'Company'}</Typography>
                                        <Typography sx={{ color: palette.inkFaint, fontSize: '.52rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' }}>Registered Company</Typography>
                                    </Box>
                                    <BrandLogo
                                        src={companyLogo}
                                        scope="company"
                                        size={58}
                                        radius={13}
                                        alt={data?.companyName || 'Company'}
                                        fallbackIcon
                                        imgSx={{ objectFit: 'contain', borderRadius: 0 }}
                                    />
                                </Box>
                            </motion.div>
                        </Box>

                        {/* Certificate Title */}
                        <Box sx={{ textAlign: 'center', mb: 1 }}>
                            <Typography sx={{
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                fontWeight: 600,
                                color: '#655A54',
                                letterSpacing: 4,
                                textTransform: 'uppercase',
                                mb: 0.5
                            }}>
                                Certificate of Registration
                            </Typography>
                            <Typography sx={{
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontSize: { xs: '1.4rem', sm: '1.8rem' },
                                fontWeight: 800,
                                color: '#0f172a',
                                lineHeight: 1.1,
                                mb: 0.25
                            }}>
                                {data?.companyName || 'Company Name'}
                            </Typography>
                            <Typography sx={{
                                color: '#655A54',
                                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                fontWeight: 500
                            }}>
                                is now an officially registered Company on the TELCOFAIR Network
                            </Typography>
                        </Box>

                        <Box sx={{
                            width: 60,
                            height: 2.5,
                            mx: 'auto',
                            mb: 2,
                            background: 'linear-gradient(90deg, #c1541f, #b8842a)',
                            borderRadius: 4
                        }} />

                        {/* Certificate Details */}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, minmax(0, 1fr))' },
                            gap: 1.5,
                            maxWidth: 650,
                            mx: 'auto',
                            mb: 2
                        }}>
                            {[
                                { label: 'Registration #', value: registrationNumber, color: palette.rust },
                                { label: 'Date Issued', value: certificateDate(data?.certificateGeneratedAt || data?.verificationDate || data?.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }), color: palette.pine },
                                { label: 'Company Type', value: data?.companyType || 'N/A', color: palette.gold },
                                { label: 'Membership', value: data?.isActive === false ? 'Inactive' : 'Active', color: data?.isActive === false ? palette.rust : palette.pine },
                                { label: 'Verification', value: data?.isVerified === true ? 'Verified' : 'Registered', color: data?.isVerified === true ? palette.pine : palette.gold },
                            ].map((item) => (
                                <Box key={item.label} sx={{
                                    ...glassCard({ level: 1, radius: '10px', accent: item.color }),
                                    p: 1.5,
                                    textAlign: 'center'
                                }}>
                                    <Typography sx={{ fontSize: '0.5rem', color: palette.inkFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {item.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, color: item.color, fontWeight: 800, fontFamily: 'monospace', mt: 0.25 }}>
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Full business profile */}
                        <Box sx={{
                            maxWidth: 760,
                            mx: 'auto',
                            mb: 1.8,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                            gap: 1.2,
                        }}>
                            {[
                                { title: 'Services', values: services, tone: palette.gold, empty: 'No services listed' },
                                { title: 'Region priorities', values: regionPriorities, tone: palette.teal, empty: 'No regions listed' },
                                { title: 'Looking for', values: lookingFor, tone: palette.purple, empty: 'No buying interests listed' },
                            ].map((section, index) => (
                                <motion.div
                                    key={section.title}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: .38, delay: .14 + index * .06 }}
                                >
                                    <Box sx={{
                                        ...glassCard({ level: 1, radius: '12px', accent: section.tone }),
                                        p: 1.45,
                                        minHeight: 104,
                                        height: '100%',
                                        background: `linear-gradient(145deg,#fff,${alpha(section.tone, .035)})`,
                                    }}>
                                        <Typography sx={{ fontSize: '0.52rem', color: section.tone, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.7 }}>
                                            {section.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.48 }}>
                                            {section.values.length ? section.values.map((value) => (
                                                <Chip
                                                    key={`${section.title}-${value}`}
                                                    label={value}
                                                    size="small"
                                                    sx={{
                                                        ...glassPill(section.tone),
                                                        height: 'auto',
                                                        minHeight: 20,
                                                        maxWidth: '100%',
                                                        '& .MuiChip-label': {
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                            lineHeight: 1.2,
                                                            px: .75,
                                                            py: .35,
                                                            fontSize: '0.54rem',
                                                        },
                                                    }}
                                                />
                                            )) : (
                                                <Typography sx={{ fontSize: '.6rem', color: palette.inkFaint, fontStyle: 'italic' }}>{section.empty}</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </motion.div>
                            ))}
                        </Box>

                        {/* Footer */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                            pt: 1.5,
                            borderTop: `1px solid ${palette.hairline}`
                        }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>
                                    {data?.isVerified === true ? 'Verified & Authentic' : 'Registered Member'}
                                </Typography>
                                <Typography sx={{ fontSize: '0.6rem', color: '#655A54' }}>
                                    Issued by TELCOFAIR Network
                                </Typography>
                            </Box>

                            <Box sx={{ width: 1, height: 30, bgcolor: 'rgba(33,26,20,0.06)' }} />

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                            }}>
                                <Box sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    ...glassPod(palette.gold, 24, '50%'),
                                }}>
                                    <CheckIcon sx={{ color: '#fff', fontSize: 14 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#0f172a' }}>{data?.isVerified === true ? 'Digitally Verified' : 'Registration Recorded'}</Typography>
                                    <Typography sx={{ fontSize: '0.5rem', color: '#655A54' }}>{data?.isVerified === true ? 'Verified by TELCOFAIR' : 'Verification pending'}</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ width: 1, height: 30, bgcolor: 'rgba(33,26,20,0.06)' }} />

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.6rem', color: '#655A54' }}>
                                    Registration #{registrationNumber}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Ornamental bottom border */}
                        <Box sx={{
                            height: 2.5,
                            width: '35%',
                            mx: 'auto',
                            mt: 1.5,
                            borderRadius: 4,
                            background: 'linear-gradient(90deg, transparent, #2f5942, #c1541f, transparent)',
                            opacity: 0.3
                        }} />
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                        mt: 2.5,
                        flexWrap: 'wrap',
                        pb: 1,
                    }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{ ...glassGhostButton(palette.inkSoft), px: 3, py: 1, fontSize: '0.8rem' }}
                        >
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            onClick={generateCertificatePDF}
                            disabled={downloading}
                            startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                            sx={{ ...glassButton(palette.pine, palette.pineDeep), px: 3.5, py: 1, fontSize: '0.8rem' }}
                        >
                            {downloading ? 'Downloading...' : '📄 Download Certificate'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

// ============================================================================
// GLASS SKELETON
// ============================================================================

const GlassSkeleton = memo(({ h = 16, w = '100%', radius = '8px', mb = 0 }) => (
    <Box sx={{ ...glassWell(radius), height: h, width: w, mb, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
            position: 'absolute',
            inset: 0,
            width: '38%',
            background: `linear-gradient(90deg, transparent, ${alpha(palette.paper, 0.9)}, transparent)`,
            animation: 'tfSkeleton 1.5s ease-in-out infinite',
            willChange: 'transform',
        }} />
    </Box>
));
GlassSkeleton.displayName = 'GlassSkeleton';

// ============================================================================
// MAIN COMPANY INFO COMPONENT
// ============================================================================

function CompanyInfo() {
    const navigate = useNavigate();
    const { userData, companyData, currentUser, refreshCompanyData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [company, setCompany] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [error, setError] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [coverFile, setCoverFile] = useState(null);
    const logoInputRef = useRef(null);
    const coverInputRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [showCertificate, setShowCertificate] = useState(false);
    const [certificateData, setCertificateData] = useState(null);
    const [countryAnchorEl, setCountryAnchorEl] = useState(null);
    const [canEdit, setCanEdit] = useState(false);

    // Data arrays
    const companyTypes = [
        'Operator', 'Aggregator', 'Platform Provider', 'Infrastructure',
        'Consultancy', 'Training', 'HR Services', 'Marketing Agency', 'Other'
    ];

    const businessTitles = [
        'Assistant', 'Sales Person', 'Manager', 'Director / VP', 'CEO'
    ];

    const specializations = [
        'Routing', 'Product', 'Finance', 'Marketing', 'Sales', 'IT', 'Procurement', 'Roaming'
    ];

    const regions = [
        'Europe', 'Africa', 'Asia', 'Middle East', 'USA/Canada', 'LATAM', 'Americas', 'Australia'
    ];

    const services = [
        'Voice', 'SMS', 'Platform Provider', 'IT', 'Data', 'Training/HR', 'VAS', 'Marketing', 'Other'
    ];

    injectAssets();

    // ============================================
    // CHECK IF USER CAN EDIT (Domain-based)
    // ============================================
    
    const checkCanEdit = useCallback(() => {
        const userEmail = currentUser?.email || userData?.email || '';
        const companyDomain = company?.website || company?.domain || '';
        
        // Extract domain from email
        const emailDomain = userEmail.split('@')[1]?.toLowerCase() || '';
        
        // Extract domain from company website
        let companyDomainName = '';
        if (companyDomain) {
            try {
                const url = companyDomain.startsWith('http') ? new URL(companyDomain) : new URL(`https://${companyDomain}`);
                companyDomainName = url.hostname.replace('www.', '').toLowerCase();
            } catch (e) {
                companyDomainName = companyDomain.replace('www.', '').toLowerCase();
            }
        }
        
        // Check if email domain matches company domain
        const isDomainMatch = emailDomain && companyDomainName && 
            (emailDomain === companyDomainName || 
             emailDomain.endsWith(`.${companyDomainName}`) ||
             companyDomainName.endsWith(`.${emailDomain}`));
        
        // Editing company-wide data is privileged. A matching corporate email
        // domain is useful context, but it must never grant write access by itself.
        const normalizedRole = String(userData?.role || userData?.userRole || '').toLowerCase().replace(/[\s-]+/g, '_');
        const isAdminRole = userData?.isOwner === true || ['admin', 'company_admin', 'owner'].includes(normalizedRole);
        const canEditResult = isAdminRole;
        console.log('🔍 Edit permissions:', { 
            emailDomain, 
            companyDomainName, 
            isDomainMatch, 
            isAdminRole, 
            canEditResult 
        });
        
        return canEditResult;
    }, [currentUser, userData, company]);

    // ============================================
    // FETCH COMPANY AND USER DATA
    // ============================================

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                console.log('🔍 CompanyInfo - Starting data fetch...');
                console.log('📦 companyData from context:', companyData);
                console.log('👤 userData from context:', userData);

                let companyDataObj = null;
                let userDataObj = userData || {};

                // 1. First try to use companyData from context. Both id/companyId
                // are valid shapes in the existing project.
                if (companyData && (companyData.id || companyData.companyId)) {
                    console.log('✅ Using companyData from context');
                    companyDataObj = normalizeCompanyRecord(companyData);
                }

                // 2. If no companyData, try to get from userData
                if (!companyDataObj && userData) {
                    console.log('🔍 Looking in userData for company info');
                    
                    if (userData.companyId) {
                        console.log('🔍 Fetching company by companyId:', userData.companyId);
                        try {
                            const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
                            if (companyDoc.exists()) {
                                companyDataObj = normalizeCompanyRecord(companyDoc.data(), companyDoc.id);
                                console.log('✅ Company found by companyId');
                            }
                        } catch (err) {
                            console.warn('Error fetching company by companyId:', err);
                        }
                    }
                }

                // 3. If user is a team member, get company from userData
                if (!companyDataObj && userData?.companyId && userData?.companyName) {
                    console.log('✅ User is a team member, using company info from userData');
                    companyDataObj = normalizeCompanyRecord({
                        companyName: userData.companyName,
                        ...userData
                    }, userData.companyId);
                }

                if (!companyDataObj) {
                    throw new Error('No company data found');
                }

                // Store user info separately
                setUserInfo(userDataObj);

                // Merge company data with user data for service/region fields
                const mergedData = {
                    ...companyDataObj,
                    // User fields that contain service/region info
                    primaryServiceProvided: userDataObj.primaryServiceProvided || companyDataObj.primaryServiceProvided,
                    secondaryServiceProvided: userDataObj.secondaryServiceProvided || companyDataObj.secondaryServiceProvided,
                    primaryRegionAvailability: userDataObj.primaryRegionAvailability || companyDataObj.primaryRegionAvailability,
                    secondaryRegionAvailability: userDataObj.secondaryRegionAvailability || companyDataObj.secondaryRegionAvailability,
                    preferredRegion: userDataObj.preferredRegion || companyDataObj.preferredRegion,
                    primaryServiceBuying: userDataObj.primaryServiceBuying || companyDataObj.primaryServiceBuying,
                    secondaryPreferredRegion: userDataObj.secondaryPreferredRegion || companyDataObj.secondaryPreferredRegion,
                    secondaryServiceBuying: userDataObj.secondaryServiceBuying || companyDataObj.secondaryServiceBuying,
                    specialization: userDataObj.specialization || companyDataObj.specialization,
                    linkedinProfile: userDataObj.linkedinProfile || companyDataObj.linkedinProfile,
                    title: userDataObj.title || companyDataObj.title,
                    businessTitle: userDataObj.businessTitle || companyDataObj.businessTitle,
                    phone: userDataObj.phone || companyDataObj.phone,
                    dialCode: userDataObj.dialCode || companyDataObj.dialCode,
                    country: userDataObj.country || companyDataObj.country,
                    countryCode: userDataObj.countryCode || companyDataObj.countryCode,
                    profileImage: userDataObj.profileImage || companyDataObj.profileImage,
                };

                console.log('🎯 Final merged data:', mergedData);
                setCompany(mergedData);
                setEditForm(mergedData);
                setLogoPreview(getCompanyLogoUrl(mergedData) || null);
                setCoverPreview(getCompanyCoverUrl(mergedData) || null);

                // Set certificate data with all fields
                setCertificateData({
                    ...mergedData,
                    companyName: mergedData.companyName || 'Company Name',
                    companyType: mergedData.companyType || 'Other',
                    // Keep every registration business-profile field available to
                    // the certificate. Legacy fields remain for older companies.
                    primaryService: mergedData.primaryServiceProvided || mergedData.primaryService || 'N/A',
                    secondaryService: mergedData.secondaryServiceProvided || mergedData.secondaryService || 'N/A',
                    primaryRegion: mergedData.primaryRegionAvailability || mergedData.primaryRegion || 'N/A',
                    secondaryRegion: mergedData.secondaryRegionAvailability || mergedData.secondaryRegion || 'N/A',
                    primaryConnectWith: mergedData.primaryConnectWith || 'N/A',
                    secondaryConnectWith: mergedData.secondaryConnectWith || 'N/A',
                    otherConnectWith: mergedData.otherConnectWith || '',
                    registrationNumber: mergedData.registrationNumber || '',
                    logo: getCompanyLogoUrl(mergedData) || null,
                });

            } catch (error) {
                console.error('❌ Error fetching data:', error);
                setError(friendlyError(error, 'Unable to complete this request.'));
                toast.error('Failed to load company details');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [companyData, userData]);

    // ============================================
    // CHECK EDIT PERMISSIONS AFTER DATA LOADS
    // ============================================

    useEffect(() => {
        if (company && userData) {
            const canEditResult = checkCanEdit();
            setCanEdit(canEditResult);
        }
    }, [company, userData, currentUser, checkCanEdit]);

    // ============================================
    // SEND NOTIFICATIONS
    // ============================================

    const sendNotifications = async (message, type, userIds) => {
        try {
            // Send push notifications
            for (const userId of userIds) {
                await addDoc(collection(db, 'notifications'), {
                    userId: userId,
                    type: type || 'company_update',
                    title: 'Company Update',
                    body: message,
                    read: false,
                    createdAt: serverTimestamp(),
                    createdBy: currentUser?.uid || '',
                });
            }

            // Send email notifications
            try {
                await fetch('/api/send-email-notification', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userIds: userIds,
                        subject: 'Company Update Notification',
                        message: message,
                        type: type || 'company_update'
                    })
                });
            } catch (emailErr) {
                console.warn('Email notification failed:', emailErr);
            }
        } catch (err) {
            console.warn('Notification sending failed:', err);
        }
    };

    // ============================================
    // HANDLERS
    // ============================================

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleGoBack = () => {
        navigate('/dashboard');
    };

    const handleEdit = () => {
        if (!canEdit) {
            toast.error('You do not have permission to edit this company. Only users with company email domain can edit.');
            return;
        }
        setEditDialogOpen(true);
        setLogoPreview(getCompanyLogoUrl(company || {}) || null);
        setCoverPreview(getCompanyCoverUrl(company || {}) || null);
        setLogoFile(null);
        setCoverFile(null);
        setEditForm({ ...company });
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setLogoFile(null);
        setCoverFile(null);
    };

    const handleEditChange = (field, value) => {
        setEditForm(prev => ({ ...prev, [field]: value }));
    };

    const handleLogoUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCoverUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setCoverFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setCoverPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveLogo = () => {
        setLogoPreview(null);
        setLogoFile(null);
        setEditForm(prev => ({ ...prev, logo: null }));
    };

    const handleRemoveCover = () => {
        setCoverPreview(null);
        setCoverFile(null);
        setEditForm(prev => ({ ...prev, coverImage: null }));
    };

    const handleSelectCountry = (country) => {
        setEditForm(prev => ({
            ...prev,
            country: country.name,
            countryCode: country.code,
            dialCode: country.dialCode,
        }));
        setCountryAnchorEl(null);
    };

    const uploadImage = async (file, folder) => {
        if (!file || !company?.id) return null;
        try {
            const mediaKind = folder === 'company-covers' ? 'cover' : 'logo';
            return await uploadFile({
                companyId: company.id,
                file,
                kind: 'image',
                folder,
                publicId: `company_${company.id}_${mediaKind}_${Date.now()}`,
            });
        } catch (error) {
            console.error('Error uploading image:', error);
            throw error;
        }
    };

    const handleSaveEdit = async () => {
        if (!company?.id) return;

        setSaving(true);
        try {
            let logoURL = editForm.logo;
            let coverURL = editForm.coverImage;
            let logoAsset = company.logoAsset || null;
            let coverAsset = company.coverAsset || null;

            // Upload logo if new file selected
            if (logoFile) {
                logoAsset = await uploadImage(logoFile, 'company-logos');
                logoURL = logoAsset?.url || logoURL;
                setUploadingLogo(true);
            }

            // Upload cover if new file selected
            if (coverFile) {
                coverAsset = await uploadImage(coverFile, 'company-covers');
                coverURL = coverAsset?.url || coverURL;
                setUploadingCover(true);
            }

            // Remove images if set to null
            if (editForm.logo === null) {
                logoURL = null;
                if (logoAsset) { try { await deleteFile({ companyId: company.id, asset: logoAsset }); } catch (e) { /* Ignore */ } }
                logoAsset = null;
            }

            if (editForm.coverImage === null) {
                coverURL = null;
                if (coverAsset) { try { await deleteFile({ companyId: company.id, asset: coverAsset }); } catch (e) { /* Ignore */ } }
                coverAsset = null;
            }

            const updateData = {
                companyName: editForm.companyName,
                companyType: editForm.companyType,
                description: editForm.description,
                website: editForm.website,
                country: editForm.country,
                countryCode: editForm.countryCode,
                dialCode: editForm.dialCode,
                email: editForm.email,
                phone: editForm.phone,
                linkedin: editForm.linkedin,
                logo: logoURL,
                logoUrl: logoURL,
                companyLogo: logoURL,
                companyLogoUrl: logoURL,
                logoAsset,
                coverImage: coverURL,
                coverImageUrl: coverURL,
                companyCover: coverURL,
                companyCoverUrl: coverURL,
                coverAsset,
            };

            // TELCOFAIR v2 requires company-profile edits to be validated against
            // the editor's corporate email domain. The callable also keeps the
            // profile-update notification workflow centralized on the backend.
            const result = await updateCompanyProfileSecure(company.id, updateData);
            const applied = result?.changes || updateData;

            setCompany({ ...company, ...applied });
            await refreshCompanyData?.(company.id);
            try {
                window.dispatchEvent(new CustomEvent('telcofair-company-logo-updated', { detail: { companyId: company.id, logoUrl: logoURL || '' } }));
                window.dispatchEvent(new CustomEvent('telcofair-company-cover-updated', { detail: { companyId: company.id, coverUrl: coverURL || '' } }));
            } catch (_) {}
            
            // Update certificate data
            setCertificateData(prev => ({
                ...prev,
                companyName: editForm.companyName,
                companyType: editForm.companyType,
                logo: logoURL || prev?.logo,
            }));

            toast.success('Company details updated successfully!');
            setEditDialogOpen(false);
            setLogoFile(null);
            setCoverFile(null);
        } catch (error) {
            console.error('Error updating company:', error);
            toast.error('Failed to update company details');
        } finally {
            setSaving(false);
            setUploadingLogo(false);
            setUploadingCover(false);
        }
    };

    const handleViewCertificate = () => {
        setShowCertificate(true);
    };

    // ============================================
    // RENDER
    // ============================================

    const canvasSx = {
        minHeight: '100vh',
        position: 'relative',
        bgcolor: palette.canvas,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.5,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.035%27/%3E%3C/svg%3E")',
        },
    };

    if (loading) {
        return <LoadingSpinner label="Loading company profile…" />;
    }

    if (error || !company) {
        return (
            <Box className="erp-page-shell erp-internal-page" sx={canvasSx}>
                <style>{GLOBAL_CSS}</style>
                <Header />
                <Container maxWidth={false} disableGutters sx={{ py: 6, px: { xs: 2, sm: 4, md: 6, lg: 8 }, position: 'relative', width: '100%' }}>
                    <Paper elevation={0} sx={{ ...glassCard({ level: 2, radius: '18px', accent: palette.rust }), p: 4, textAlign: 'center' }}>
                        <Box sx={{ position: 'relative', width: 96, height: 96, mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {[0, 1].map((i) => (
                                <Box key={i} sx={{
                                    position: 'absolute', width: 72, height: 72, borderRadius: '50%',
                                    border: `1.5px solid ${palette.inkFaint}`, opacity: 0,
                                    animation: `tfSignalWave 3.2s ease-out ${i * 1.1}s infinite`,
                                }} />
                            ))}
                            <Box sx={{ ...glassPod(palette.inkFaint, 72, '50%'), opacity: 0.85 }}>
                                <BusinessIcon sx={{ fontSize: 34 }} />
                            </Box>
                        </Box>
                        <Typography variant="h5" sx={{ color: palette.ink, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                            Company Not Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 1 }}>
                            {error || 'No company data available. Please complete registration.'}
                        </Typography>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleGoBack}
                            sx={{ ...glassButton(palette.rust, palette.rustDeep), mt: 3, px: 4, py: 1.2 }}
                        >
                            Back to Dashboard
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    // ============================================
    // EXTRACT DATA FROM MERGED COMPANY OBJECT
    // ============================================
    
    const companyName = company.companyName || company.name || 'My Company';
    const companyType = company.companyType?.replace(/_/g, ' ') || company.companyType || 'N/A';
    
    // Services - from user data (primaryServiceProvided)
    const primaryService = Array.isArray(company.primaryServiceProvided) 
        ? company.primaryServiceProvided[0] 
        : company.primaryServiceProvided || company.primaryService || 'N/A';
    
    const secondaryService = Array.isArray(company.secondaryServiceProvided) 
        ? company.secondaryServiceProvided[0] 
        : company.secondaryServiceProvided || company.secondaryService || 'N/A';
    
    // Regions - from user data (primaryRegionAvailability)
    const primaryRegion = Array.isArray(company.primaryRegionAvailability) 
        ? company.primaryRegionAvailability[0] 
        : company.primaryRegionAvailability || company.primaryRegion || 'N/A';
    
    const secondaryRegion = Array.isArray(company.secondaryRegionAvailability) 
        ? company.secondaryRegionAvailability[0] 
        : company.secondaryRegionAvailability || company.secondaryRegion || 'N/A';
    
    // Connect With
    const primaryConnectWith = company.primaryConnectWith || 'N/A';
    const secondaryConnectWith = company.secondaryConnectWith || 'N/A';
    const otherConnectWith = company.otherConnectWith || '';
    
    // Buying interests
    const preferredRegion = Array.isArray(company.preferredRegion) 
        ? company.preferredRegion[0] 
        : company.preferredRegion || 'N/A';
    
    const primaryServiceBuying = Array.isArray(company.primaryServiceBuying) 
        ? company.primaryServiceBuying[0] 
        : company.primaryServiceBuying || 'N/A';
    
    const secondaryPreferredRegion = Array.isArray(company.secondaryPreferredRegion) 
        ? company.secondaryPreferredRegion[0] 
        : company.secondaryPreferredRegion || 'N/A';
    
    const secondaryServiceBuying = Array.isArray(company.secondaryServiceBuying) 
        ? company.secondaryServiceBuying[0] 
        : company.secondaryServiceBuying || 'N/A';
    
    // Basic company fields
    const country = company.country || 'N/A';
    const countryCode = company.countryCode || company.country_code || '';
    const dialCode = company.dialCode || company.dial_code || '';
    const email = company.email || 'N/A';
    const phone = company.phone || 'N/A';
    const website = company.website || '';
    const description = company.description || 'No description provided.';
    const logo = getCompanyLogoUrl(company || {}) || null;
    const coverImage = getCompanyCoverUrl(company || {}) || null;
    const specialization = company.specialization || company.specializations || [];
    const businessTitle = company.businessTitle || company.business_title || 'N/A';

    // User data
    const userEmail = currentUser?.email || userData?.email || '';
    const userFullName = userData?.displayName || `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || 'User';
    const userRole = userData?.role || 'employee';
    const userTitle = userData?.title || '';
    const userLinkedin = userData?.linkedinProfile || '';
    const userPhone = userData?.phone || '';
    const userDialCode = userData?.dialCode || '';
    const userCountry = userData?.country || '';
    const userProfileImage = userData?.profileImage || null;
    const accentColor = palette.rust;
    const accentGradient = `linear-gradient(135deg, ${palette.rust}, ${palette.pine})`;

    const coverGradient = `linear-gradient(135deg, ${palette.rust} 0%, ${palette.pine} 100%)`;


    const selectedCountry = {
        code: countryCode || 'US',
        dialCode: dialCode || '+1',
        name: country || 'United States',
        flag: '🇺🇸',
    };

    const isAdmin = userRole === 'admin' || userRole === 'owner';

    const hasServices = primaryService !== 'N/A' || secondaryService !== 'N/A';
    const hasRegions = primaryRegion !== 'N/A' || secondaryRegion !== 'N/A';
    const hasConnectWith = primaryConnectWith !== 'N/A' || secondaryConnectWith !== 'N/A' || otherConnectWith;
    const hasBuyingInterests = preferredRegion !== 'N/A' || primaryServiceBuying !== 'N/A';

    return (
        <Box className="erp-page-shell erp-internal-page" sx={canvasSx}>
            <Header />

            <Container maxWidth={false} disableGutters sx={{
                py: { xs: 3, md: 4.5 },
                px: { xs: 2, sm: 3, md: 4, lg: 6 },
                position: 'relative',
                width: '100%',
                maxWidth: '1600px',
                mx: 'auto',
                zIndex: 1,
            }}>
                <Box className="erp-page-hero" sx={{ mb: 2.5, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box>
                        <Typography className="erp-page-title">Company Workspace</Typography>
                        <Typography className="erp-page-subtitle">Manage your company profile, services, branding and public information.</Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>Dashboard</Button>
                </Box>


                {/* Back Button */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                    sx={{ ...glassGhostButton(palette.inkSoft), mb: 3, px: 2.5, py: 0.8, fontSize: '0.8rem' }}
                >
                    Back to Dashboard
                </Button>

                {/* Company Header with Cover Image */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box sx={{ ...glassCard({ level: 3, radius: '18px', accent: accentColor }), mb: 2.5, overflow: 'hidden', p: 0 }}>
                        <Box className="tf-fixed-cover tf-profile-cover" sx={{
                            position: 'relative',
                            height: { xs: 120, md: 140 },
                            width: '100%',
                            background: coverImage ? `url(${coverImage}) center/cover` : coverGradient,
                            transition: 'opacity .25s ease'
                        }}>
                            {!coverImage && (
                                <Box sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <ImageIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.2)' }} />
                                </Box>
                            )}
                            <Box sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '40%',
                                background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.3))',
                            }} />
                            {/* Status Badge on Cover */}
                            <Box sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                zIndex: 2,
                                display: 'flex',
                                gap: 0.5,
                            }}>
                                {company.isVerified && (
                                    <Tooltip title="Verified Company">
                                        <Box sx={glassPod(palette.pine, 32, '50%')}>
                                            <VerifiedIcon sx={{ fontSize: 16 }} />
                                        </Box>
                                    </Tooltip>
                                )}
                                <PremiumStatusChip status={company.isBlocked ? 'blocked' : company.isActive ? 'active' : 'pending'} />
                            </Box>
                        </Box>

                        <Box sx={{ p: { xs: 3, md: 4 }, pt: 0 }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'flex-start', md: 'center' },
                                gap: 3,
                                position: 'relative',
                                mt: -3,
                            }}>
                                <Avatar
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '14px',
                                        background: accentGradient,
                                        boxShadow: `0 8px 24px ${accentColor}40`,
                                        fontWeight: 700,
                                        fontSize: '2rem',
                                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                        color: '#fff',
                                        border: `4px solid ${palette.paper}`,
                                    }}
                                >
                                    {logo ? (
                                        <img src={logo} alt={companyName} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 7, borderRadius: '14px', background: '#fff' }} />
                                    ) : (
                                        companyName?.charAt(0) || 'C'
                                    )}
                                </Avatar>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                                fontWeight: 700,
                                                color: palette.ink,
                                                fontSize: { xs: '1.8rem', md: '2.2rem' },
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {companyName}
                                        </Typography>
                                        {company.isVerified && (
                                            <Tooltip title="Verified Company">
                                                <VerifiedIcon sx={{ color: palette.pine, fontSize: 28 }} />
                                            </Tooltip>
                                        )}
                                        {isAdmin && (
                                            <Chip
                                                label="Admin"
                                                size="small"
                                                sx={{
                                                    ...glassPill(palette.rust, true),
                                                    height: 24,
                                                    fontSize: '0.6rem',
                                                }}
                                            />
                                        )}
                                        {canEdit && (
                                            <Chip
                                                label="Can Edit"
                                                size="small"
                                                sx={{
                                                    ...glassPill(palette.teal, true),
                                                    height: 24,
                                                    fontSize: '0.6rem',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                        {country && country !== 'N/A' && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: palette.inkSoft }}>
                                                <LocationIcon sx={{ fontSize: 16 }} />
                                                <Typography variant="body2">{country}</Typography>
                                            </Box>
                                        )}
                                        <Chip
                                            label={companyType}
                                            size="small"
                                            sx={{
                                                ...glassPill(accentColor),
                                                height: 24,
                                                fontSize: '0.65rem',
                                            }}
                                        />
                                        {primaryService && primaryService !== 'N/A' && (
                                            <Chip
                                                label={`Service: ${primaryService}`}
                                                size="small"
                                                sx={{
                                                    ...glassPill(palette.gold),
                                                    height: 24,
                                                    fontSize: '0.65rem',
                                                }}
                                            />
                                        )}
                                        {userRole && (
                                            <Chip
                                                label={`Role: ${userRole.charAt(0).toUpperCase() + userRole.slice(1)}`}
                                                size="small"
                                                sx={{
                                                    ...glassPill(palette.teal),
                                                    height: 24,
                                                    fontSize: '0.6rem',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {canEdit && (
                                        <Button
                                            startIcon={<EditIcon />}
                                            onClick={handleEdit}
                                            sx={{ ...glassGhostButton(palette.teal), px: 2.5, py: 0.8, fontSize: '0.8rem' }}
                                        >
                                            Edit Profile
                                        </Button>
                                    )}
                                    <Button
                                        startIcon={<PremiumIcon />}
                                        onClick={handleViewCertificate}
                                        sx={{ ...glassButton(palette.gold, palette.goldDeep), px: 2.5, py: 0.8, fontSize: '0.8rem' }}
                                    >
                                        View Certificate
                                    </Button>
                                    {isAdmin && (
                                        <Button
                                            startIcon={<SettingsIcon />}
                                            onClick={() => navigate('/dashboard/company/settings')}
                                            sx={{ ...glassButton(palette.rust, palette.rustDeep), px: 2.5, py: 0.8, fontSize: '0.8rem' }}
                                        >
                                            Settings
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </motion.div>

                {/* Stats Cards */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                    gap: 2.5,
                    width: '100%',
                    mb: 4,
                }}>
                    {[
                        { title: 'Company Type', value: companyType, icon: <BusinessIcon />, overlayIcon: <BusinessIcon />, color: palette.rust },
                        { title: 'Primary Service', value: primaryService, icon: <StarsIcon />, overlayIcon: <StarsIcon />, color: palette.gold },
                        { title: 'Primary Region', value: primaryRegion, icon: <PublicIcon />, overlayIcon: <PublicIcon />, color: palette.teal },
                        { title: 'Connect With', value: primaryConnectWith, icon: <HandshakeIcon />, overlayIcon: <HandshakeIcon />, color: palette.pine },
                    ].map((stat, index) => (
                        <PremiumStatCard
                            key={stat.title}
                            title={stat.title}
                            value={typeof stat.value === 'string' && stat.value !== 'N/A' ? 1 : 0}
                            icon={stat.icon}
                            overlayIcon={stat.overlayIcon}
                            color={stat.color}
                            delay={index * 0.06}
                            loading={loading}
                        />
                    ))}
                </Box>

                {/* Tabs Section */}
                <Paper elevation={0} sx={{
                    ...glassCard({ level: 2, radius: '18px', accent: palette.rust }),
                    p: { xs: 2.5, md: 3.5 },
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, width: '100%' }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    fontSize: '0.82rem',
                                    minHeight: 44,
                                    px: 2,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: palette.rust,
                                    }
                                },
                                '& .Mui-selected': {
                                    color: palette.rust,
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: palette.rust,
                                }
                            }}
                        >
                            <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
                            <Tab label="Services & Regions" icon={<StorefrontIcon />} iconPosition="start" />
                            <Tab label="Connect With" icon={<HandshakeIcon />} iconPosition="start" />
                            <Tab label="Buying Interests" icon={<ShoppingCartIcon />} iconPosition="start" />
                            <Tab label="Contact" icon={<EmailIcon />} iconPosition="start" />
                            <Tab label="My Profile" icon={<PersonIcon />} iconPosition="start" />
                        </Tabs>
                    </Box>

                    {/* ================================ */}
                    {/* OVERVIEW TAB */}
                    {/* ================================ */}
                    {tabValue === 0 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                About {companyName}
                            </Typography>
                            <Typography variant="body1" sx={{ color: palette.inkSoft, mb: 3, lineHeight: 1.8 }}>
                                {description}
                            </Typography>

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Company Details
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<BusinessIcon />}
                                        label="Company"
                                        value={companyName}
                                        color={palette.rust}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<FlagIcon />}
                                        label="Country"
                                        value={country}
                                        color={palette.pine}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<EmailIcon />}
                                        label="Company Email"
                                        value={email}
                                        color={palette.teal}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<PhoneIcon />}
                                        label="Company Phone"
                                        value={dialCode ? `${dialCode} ${phone}` : phone}
                                        color={palette.gold}
                                    />
                                </Grid>
                                {businessTitle && businessTitle !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<WorkIcon />}
                                            label="Business Title"
                                            value={businessTitle}
                                            color={palette.purple}
                                        />
                                    </Grid>
                                )}
                                {website && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<LanguageIcon />}
                                            label="Website"
                                            value={
                                                <a href={safeExternalUrl(website, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: palette.rust, textDecoration: 'none' }}>
                                                    {website}
                                                </a>
                                            }
                                            color={palette.purple}
                                        />
                                    </Grid>
                                )}
                                {specialization && specialization.length > 0 && (
                                    <Grid size={{ xs: 12 }}>
                                        <Box sx={{
                                            ...glassCard({ level: 1, radius: '14px' }),
                                            p: 2,
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.75,
                                        }}>
                                            <Typography sx={{ width: '100%', fontSize: '0.65rem', color: palette.inkFaint, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
                                                Specializations
                                            </Typography>
                                            {specialization.map((spec, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={spec}
                                                    size="small"
                                                    sx={{
                                                        ...glassPill(palette.amber),
                                                        height: 24,
                                                        fontSize: '0.6rem',
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}

                    {/* ================================ */}
                    {/* SERVICES & REGIONS TAB */}
                    {/* ================================ */}
                    {tabValue === 1 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Services Provided
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<StarsIcon />}
                                        label="Primary Service"
                                        value={primaryService}
                                        color={palette.gold}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<DiamondIcon />}
                                        label="Secondary Service"
                                        value={secondaryService}
                                        color={palette.purple}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Regions Available
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<PublicIcon />}
                                        label="Primary Region"
                                        value={primaryRegion}
                                        color={palette.teal}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<PublicIcon />}
                                        label="Secondary Region"
                                        value={secondaryRegion}
                                        color={palette.pine}
                                    />
                                </Grid>
                            </Grid>

                            {hasServices && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: palette.inkSoft, mb: 1.5 }}>
                                        All Services
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {primaryService !== 'N/A' && (
                                            <Chip
                                                label={primaryService}
                                                sx={{
                                                    ...glassPill(palette.gold),
                                                    height: 28,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        )}
                                        {secondaryService !== 'N/A' && (
                                            <Chip
                                                label={secondaryService}
                                                sx={{
                                                    ...glassPill(palette.purple),
                                                    height: 28,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {hasRegions && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: palette.inkSoft, mb: 1.5 }}>
                                        All Regions
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {primaryRegion !== 'N/A' && (
                                            <Chip
                                                label={primaryRegion}
                                                sx={{
                                                    ...glassPill(palette.teal),
                                                    height: 28,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        )}
                                        {secondaryRegion !== 'N/A' && (
                                            <Chip
                                                label={secondaryRegion}
                                                sx={{
                                                    ...glassPill(palette.pine),
                                                    height: 28,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {!hasServices && !hasRegions && (
                                <Alert severity="info" sx={{ mt: 2, ...glassWell('12px') }}>
                                    No services or regions have been added yet.
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* ================================ */}
                    {/* CONNECT WITH TAB */}
                    {/* ================================ */}
                    {tabValue === 2 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Connect With
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<HandshakeIcon />}
                                        label="Primary Connect With"
                                        value={primaryConnectWith}
                                        color={palette.pine}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<GroupIcon />}
                                        label="Secondary Connect With"
                                        value={secondaryConnectWith}
                                        color={palette.purple}
                                    />
                                </Grid>
                            </Grid>

                            {otherConnectWith && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: palette.inkSoft, mb: 1.5 }}>
                                        Other Connect With
                                    </Typography>
                                    <Chip
                                        label={otherConnectWith}
                                        sx={{
                                            ...glassPill(palette.gold),
                                            height: 28,
                                            fontSize: '0.7rem',
                                        }}
                                    />
                                </Box>
                            )}

                            {hasConnectWith && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: palette.inkSoft, mb: 1.5 }}>
                                        All Connect With Interests
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {primaryConnectWith !== 'N/A' && (
                                            <Chip
                                                label={primaryConnectWith}
                                                sx={{
                                                    ...glassPill(palette.pine),
                                                    height: 28,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        )}
                                        {secondaryConnectWith !== 'N/A' && (
                                            <Chip
                                                label={secondaryConnectWith}
                                                sx={{
                                                    ...glassPill(palette.purple),
                                                    height: 28,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        )}
                                        {otherConnectWith && (
                                            <Chip
                                                label={otherConnectWith}
                                                sx={{
                                                    ...glassPill(palette.gold),
                                                    height: 28,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {!hasConnectWith && (
                                <Alert severity="info" sx={{ mt: 2, ...glassWell('12px') }}>
                                    No connect with interests have been added yet.
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* ================================ */}
                    {/* BUYING INTERESTS TAB */}
                    {/* ================================ */}
                    {tabValue === 3 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Buying Interests
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<PublicIcon />}
                                        label="Preferred Region"
                                        value={preferredRegion}
                                        color={palette.teal}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<ShoppingCartIcon />}
                                        label="Services Interested In"
                                        value={primaryServiceBuying}
                                        color={palette.gold}
                                    />
                                </Grid>
                                {secondaryPreferredRegion && secondaryPreferredRegion !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<MapIcon />}
                                            label="Secondary Preferred Region"
                                            value={secondaryPreferredRegion}
                                            color={palette.pine}
                                        />
                                    </Grid>
                                )}
                                {secondaryServiceBuying && secondaryServiceBuying !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<CategoryIcon />}
                                            label="Secondary Services Interested In"
                                            value={secondaryServiceBuying}
                                            color={palette.purple}
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            {!hasBuyingInterests && (
                                <Alert severity="info" sx={{ mt: 2, ...glassWell('12px') }}>
                                    No buying interests have been added yet.
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* ================================ */}
                    {/* CONTACT TAB */}
                    {/* ================================ */}
                    {tabValue === 4 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Contact Information
                            </Typography>
                            <Grid container spacing={2}>
                                {email && email !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<EmailIcon />}
                                            label="Email"
                                            value={email}
                                            color={palette.rust}
                                        />
                                    </Grid>
                                )}
                                {phone && phone !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<PhoneIcon />}
                                            label="Phone"
                                            value={dialCode ? `${dialCode} ${phone}` : phone}
                                            color={palette.teal}
                                        />
                                    </Grid>
                                )}
                                {website && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<LanguageIcon />}
                                            label="Website"
                                            value={
                                                <a href={safeExternalUrl(website, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: palette.rust, textDecoration: 'none' }}>
                                                    {website}
                                                </a>
                                            }
                                            color={palette.purple}
                                        />
                                    </Grid>
                                )}
                                {country && country !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<LocationIcon />}
                                            label="Country"
                                            value={country}
                                            color={palette.pine}
                                        />
                                    </Grid>
                                )}
                                {dialCode && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<PhoneIcon />}
                                            label="Dial Code"
                                            value={dialCode}
                                            color={palette.amber}
                                        />
                                    </Grid>
                                )}
                                {company.linkedin && (
                                    <Grid size={{ xs: 12 }}>
                                        <InfoCard
                                            icon={<LinkedInIcon />}
                                            label="LinkedIn"
                                            value={
                                                <a href={safeExternalUrl(company.linkedin, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: palette.rust, textDecoration: 'none' }}>
                                                    View Profile
                                                </a>
                                            }
                                            color={palette.linkedin}
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            {(email === 'N/A' && phone === 'N/A' && !website) && (
                                <Typography variant="body2" sx={{ color: palette.inkSoft }}>
                                    No contact information available.
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* ================================ */}
                    {/* MY PROFILE TAB */}
                    {/* ================================ */}
                    {tabValue === 5 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                My Profile
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<PersonIcon />}
                                        label="Full Name"
                                        value={userFullName}
                                        color={palette.rust}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<EmailIcon />}
                                        label="Email"
                                        value={userEmail}
                                        color={palette.teal}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<WorkIcon />}
                                        label="Role"
                                        value={userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                                        color={palette.purple}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<TitleIcon />}
                                        label="Title"
                                        value={userTitle || businessTitle || 'N/A'}
                                        color={palette.gold}
                                    />
                                </Grid>
                                {userPhone && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<PhoneIcon />}
                                            label="Phone"
                                            value={userDialCode ? `${userDialCode} ${userPhone}` : userPhone}
                                            color={palette.pine}
                                        />
                                    </Grid>
                                )}
                                {userCountry && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<LocationIcon />}
                                            label="Country"
                                            value={userCountry}
                                            color={palette.teal}
                                        />
                                    </Grid>
                                )}
                                {userLinkedin && (
                                    <Grid size={{ xs: 12 }}>
                                        <InfoCard
                                            icon={<LinkedInIcon />}
                                            label="LinkedIn"
                                            value={
                                                <a href={safeExternalUrl(userLinkedin, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: palette.rust, textDecoration: 'none' }}>
                                                    View Profile
                                                </a>
                                            }
                                            color={palette.linkedin}
                                        />
                                    </Grid>
                                )}
                                {specialization && specialization.length > 0 && (
                                    <Grid size={{ xs: 12 }}>
                                        <Box sx={{
                                            ...glassCard({ level: 1, radius: '14px' }),
                                            p: 2,
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.75,
                                        }}>
                                            <Typography sx={{ width: '100%', fontSize: '0.65rem', color: palette.inkFaint, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
                                                My Specializations
                                            </Typography>
                                            {specialization.map((spec, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={spec}
                                                    size="small"
                                                    sx={{
                                                        ...glassPill(palette.amber),
                                                        height: 24,
                                                        fontSize: '0.6rem',
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </Paper>
            </Container>

            {/* ================================ */}
            {/* EDIT DIALOG */}
            {/* ================================ */}
            {canEdit && (
                <Dialog
                    open={editDialogOpen}
                    onClose={handleEditClose}
                    maxWidth="md"
                    fullWidth
                    
                    slotProps={{ backdrop: { 
                        sx: { 
                            backdropFilter: 'blur(6px)', 
                            WebkitBackdropFilter: 'blur(6px)', 
                            backgroundColor: alpha(palette.ink, 0.3) 
                        } 
                    }, ...({ paper: { 
                        sx: { 
                            ...glassCard({ level: 4, radius: '18px', accent: palette.rust }), 
                            overflow: 'hidden', 
                            maxHeight: '90vh',
                            width: '100%',
                            m: 2,
                        } 
                    } }) }}
                >
                    <DialogTitle sx={{
                        borderBottom: `1px solid ${palette.hairline}`,
                        pb: 2,
                        px: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                        fontWeight: 600,
                        color: palette.ink,
                        background: palette.canvasAlt,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={glassPod(palette.rust, 40, '12px')}>
                                <EditIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                                    Edit Company Profile
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                    Update your company information and images
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleEditClose} sx={{ color: palette.inkSoft }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ pt: 3, px: 3, pb: 1, overflowY: 'auto' }}>
                        {/* Image Upload Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink, mb: 2, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                                Company Images
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{
                                        ...glassCard({ level: 1, radius: '16px', accent: palette.rust }),
                                        p: 2,
                                        textAlign: 'center',
                                    }}>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Company Logo
                                        </Typography>
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                height: 150,
                                                mt: 1.5,
                                                borderRadius: '12px',
                                                overflow: 'hidden',
                                                border: `2px dashed ${logoPreview ? palette.rust : palette.hairline}`,
                                                background: logoPreview ? `url(${logoPreview}) center/cover` : 'rgba(33,26,20,0.02)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    borderColor: palette.rust,
                                                    ...glassLift(2, palette.rust),
                                                }
                                            }}
                                            onClick={() => logoInputRef.current?.click()}
                                        >
                                            {!logoPreview ? (
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <AddPhotoIcon sx={{ fontSize: 40, color: palette.inkSoft, opacity: 0.4 }} />
                                                    <Typography variant="caption" sx={{ color: palette.inkSoft, display: 'block', mt: 0.5 }}>
                                                        Click to upload logo
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <>
                                                    <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: 8,
                                                            right: 8,
                                                            display: 'flex',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <Tooltip title="Change Logo">
                                                            <IconButton
                                                                size="small"
                                                                sx={{
                                                                    ...glassPod(palette.rust, 28, '50%'),
                                                                    '&:hover': { transform: 'scale(1.1)' }
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    logoInputRef.current?.click();
                                                                }}
                                                            >
                                                                <PhotoCameraIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Remove Logo">
                                                            <IconButton
                                                                size="small"
                                                                sx={{
                                                                    ...glassPod(palette.clay, 28, '50%'),
                                                                    '&:hover': { transform: 'scale(1.1)' }
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveLogo();
                                                                }}
                                                            >
                                                                <CloseIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </>
                                            )}
                                            <input
                                                ref={logoInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleLogoUpload}
                                                style={{ display: 'none' }}
                                            />
                                        </Box>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, mt: 1, display: 'block' }}>
                                            {logoPreview ? 'Logo uploaded ✓' : 'No logo uploaded'}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{
                                        ...glassCard({ level: 1, radius: '16px', accent: palette.teal }),
                                        p: 2,
                                        textAlign: 'center',
                                    }}>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Cover Image
                                        </Typography>
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                height: 150,
                                                mt: 1.5,
                                                borderRadius: '12px',
                                                overflow: 'hidden',
                                                border: `2px dashed ${coverPreview ? palette.teal : palette.hairline}`,
                                                background: coverPreview ? `url(${coverPreview}) center/cover` : 'rgba(33,26,20,0.02)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    borderColor: palette.teal,
                                                    ...glassLift(2, palette.teal),
                                                }
                                            }}
                                            onClick={() => coverInputRef.current?.click()}
                                        >
                                            {!coverPreview ? (
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <ImageIcon sx={{ fontSize: 40, color: palette.inkSoft, opacity: 0.4 }} />
                                                    <Typography variant="caption" sx={{ color: palette.inkSoft, display: 'block', mt: 0.5 }}>
                                                        Click to upload cover
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <>
                                                    <img src={coverPreview} alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: 8,
                                                            right: 8,
                                                            display: 'flex',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <Tooltip title="Change Cover">
                                                            <IconButton
                                                                size="small"
                                                                sx={{
                                                                    ...glassPod(palette.teal, 28, '50%'),
                                                                    '&:hover': { transform: 'scale(1.1)' }
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    coverInputRef.current?.click();
                                                                }}
                                                            >
                                                                <PhotoCameraIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Remove Cover">
                                                            <IconButton
                                                                size="small"
                                                                sx={{
                                                                    ...glassPod(palette.clay, 28, '50%'),
                                                                    '&:hover': { transform: 'scale(1.1)' }
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveCover();
                                                                }}
                                                            >
                                                                <CloseIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </>
                                            )}
                                            <input
                                                ref={coverInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleCoverUpload}
                                                style={{ display: 'none' }}
                                            />
                                        </Box>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, mt: 1, display: 'block' }}>
                                            {coverPreview ? 'Cover image uploaded ✓' : 'No cover image uploaded'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {/* Form Fields */}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Company Name"
                                    value={editForm.companyName || ''}
                                    onChange={(e) => handleEditChange('companyName', e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={3}
                                    value={editForm.description || ''}
                                    onChange={(e) => handleEditChange('description', e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Company Type</InputLabel>
                                    <Select
                                        value={editForm.companyType || ''}
                                        onChange={(e) => handleEditChange('companyType', e.target.value)}
                                        label="Company Type"
                                        sx={{
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }}
                                    >
                                        {companyTypes.map((type) => (
                                            <MenuItem key={type} value={type}>{type}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Website"
                                    value={editForm.website || ''}
                                    onChange={(e) => handleEditChange('website', e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Button
                                    fullWidth
                                    onClick={(e) => setCountryAnchorEl(e.currentTarget)}
                                    sx={{
                                        ...glassWell('12px'),
                                        height: '54px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: 2,
                                        textTransform: 'none',
                                        color: palette.ink,
                                        fontWeight: 500,
                                        fontSize: '0.85rem',
                                        '&:hover': {
                                            borderColor: alpha(palette.rust, 0.4),
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 10px 24px rgba(${SHADOW},0.11), inset 0 1px 0 rgba(255,255,255,0.7)`,
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {editForm.country ? (
                                            <>
                                                <Typography sx={{ fontSize: 20 }}>{selectedCountry.flag}</Typography>
                                                <Typography sx={{ fontWeight: 500, color: palette.ink, fontSize: '0.8rem' }}>
                                                    {editForm.country}
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <FlagIcon sx={{ color: palette.gold, fontSize: 18 }} />
                                                <Typography sx={{ color: palette.inkFaint, fontSize: '0.8rem' }}>Select Country</Typography>
                                            </>
                                        )}
                                    </Box>
                                    <KeyboardArrowDownIcon sx={{ color: palette.inkFaint, fontSize: 16 }} />
                                </Button>
                                <CountrySelector
                                    open={Boolean(countryAnchorEl)}
                                    anchorEl={countryAnchorEl}
                                    onClose={() => setCountryAnchorEl(null)}
                                    onSelect={handleSelectCountry}
                                    selectedCountry={selectedCountry}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={editForm.email || ''}
                                    onChange={(e) => handleEditChange('email', e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Phone"
                                    value={editForm.phone || ''}
                                    onChange={(e) => handleEditChange('phone', e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="LinkedIn URL"
                                    value={editForm.linkedin || ''}
                                    onChange={(e) => handleEditChange('linkedin', e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{
                        p: 3,
                        borderTop: `1px solid ${palette.hairline}`,
                        gap: 1.5,
                        bgcolor: palette.canvasAlt,
                    }}>
                        <Button onClick={handleEditClose} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 1 }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveEdit}
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ ...glassButton(palette.rust, palette.rustDeep), px: 3, py: 1 }}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Certificate Dialog */}
            {showCertificate && certificateData && (
                <CertificateView
                    data={certificateData}
                    onClose={() => setShowCertificate(false)}
                />
            )}

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(0.8); }
                }
                @keyframes tfFloatIcon {
                    0%, 100% { transform: translate(0, 0) rotate(0deg); }
                    50% { transform: translate(-10px, -10px) rotate(5deg); }
                }
            `}</style>
        </Box>
    );
}

export default CompanyInfo;